import { useParams } from "@tanstack/react-router"
import { useEffect } from "react"
import { useGetSettingsQuery, useGetTenantsQuery } from "./api"
import { getTenantSlug, setTenantSlug } from "@/auth"
import { useDispatch, useSelector } from "@/base"

const isAnswered = ({
  isSuccess,
  isError,
}: {
  isSuccess: boolean
  isError: boolean
}) => isSuccess || isError

/**
 * Puts the workspace the page is about into the store, which every request
 * takes its Tenant-Slug from. False until it's there: anything rendered sooner
 * sends its requests for no workspace or the previous one.
 */
export default function useTenantScope(isAuthenticated: boolean): boolean {
  const dispatch = useDispatch()
  const { tenantSlug: urlSlug } = useParams({ strict: false })
  const storedSlug = useSelector(getTenantSlug)
  const settings = useGetSettingsQuery(undefined, { skip: !isAuthenticated })
  const tenants = useGetTenantsQuery(undefined, { skip: !isAuthenticated })

  // Settings must answer first: a stored fallback outranks it from then on
  const firstMembership = isAnswered(settings)
    ? tenants.data?.[0]?.slug
    : undefined
  const slug =
    urlSlug ?? storedSlug ?? settings.data?.currentTenantSlug ?? firstMembership

  useEffect(() => {
    if (isAuthenticated && slug && slug !== storedSlug) {
      dispatch(setTenantSlug(slug))
    }
  }, [dispatch, isAuthenticated, slug, storedSlug])

  if (slug) {
    return slug === storedSlug
  }

  // A user without any workspace, once both answers say so
  return isAnswered(settings) && isAnswered(tenants)
}
